/**
 * Composable для управления Swiper в компоненте заказов
 * 
 * Отвечает за:
 * - Инициализацию и конфигурацию Swiper
 * - Синхронизацию активного слайда с выбранным заказом
 * - Обработку взаимодействий пользователя со слайдером
 * - Адаптивное поведение (включение/выключение свайпа)
 */

import { ref, computed, nextTick, type Ref } from 'vue'
import { consola } from 'consola'
import { SWIPER_CONFIG } from '@shared/config'
import type { OrderSummary } from '@entities/order'

const logger = consola.withTag('order-swiper')

/**
 * Интерфейс Swiper instance (упрощенный)
 */
interface SwiperInstance {
  activeIndex?: number
  realIndex?: number
  slideTo: (index: number, speed?: number) => void
  updateSlides?: () => void
  updateSize?: () => void
  update?: () => void
}

/**
 * Composable для управления Swiper
 */
export function useOrderSwiper(
  navigationOrders: Ref<OrderSummary[]>,
  currentOrderId: Ref<string | number | undefined>,
) {
  // Refs
  const swiperContainerRef = ref<any>(null)
  const currentSlideIndex = ref<number>(0)
  const isSwiperUpdating = ref(false)
  const swiperKey = ref<number>(1)

  // Computed
  const navigationCount = computed(() => navigationOrders.value.length)
  const hasMultipleOrders = computed(() => navigationCount.value > 1)

  /**
   * Индекс текущего заказа в массиве
   */
  const currentOrderIndex = computed(() => {
    if (!currentOrderId.value || navigationOrders.value.length === 0) return 0
    const index = navigationOrders.value.findIndex(
      (o) => String(o.id) === String(currentOrderId.value),
    )
    return index >= 0 ? index : 0
  })

  /**
   * Конфигурация Swiper на основе количества заказов
   */
  const swiperConfig = computed(() => ({
    loop: false,
    slidesPerView: 1,
    spaceBetween: 0,
    allowTouchMove: hasMultipleOrders.value,
    allowSlideNext: hasMultipleOrders.value,
    allowSlidePrev: hasMultipleOrders.value,
    touchRatio: hasMultipleOrders.value ? 1 : 0,
    touchAngle: SWIPER_CONFIG.TOUCH_ANGLE,
    grabCursor: hasMultipleOrders.value,
    resistance: hasMultipleOrders.value,
    resistanceRatio: SWIPER_CONFIG.RESISTANCE_RATIO,
  }))

  /**
   * Breakpoints для адаптивности
   */
  const swiperBreakpoints = computed(() => 
    hasMultipleOrders.value ? SWIPER_CONFIG.BREAKPOINTS : undefined,
  )

  /**
   * CSS классы для Swiper
   */
  const swiperClasses = computed(() => ({
    'swiper-disabled': !hasMultipleOrders.value,
  }))

  /**
   * Получает экземпляр Swiper
   */
  function getSwiperInstance(): SwiperInstance | null {
    return swiperContainerRef.value?.swiper || null
  }

  /**
   * Выполняет действие со Swiper с защитой от обновлений
   */
  async function withSwiperUpdateLock<T>(
    action: () => Promise<T> | T,
    delay: number = SWIPER_CONFIG.UPDATE_DELAY,
  ): Promise<T | null> {
    if (isSwiperUpdating.value) {
      logger.warn('Swiper is already updating, skipping action')
      return null
    }

    isSwiperUpdating.value = true
    try {
      const result = await action()
      await nextTick()
      setTimeout(() => {
        isSwiperUpdating.value = false
      }, delay)
      return result
    } catch (error) {
      logger.error('Error during swiper action:', error)
      isSwiperUpdating.value = false
      return null
    }
  }

  /**
   * Обновляет Swiper instance
   */
  async function refreshSwiperInstance(shouldSync = false): Promise<void> {
    await nextTick()

    const swiper = getSwiperInstance()
    if (!swiper) {
      return
    }

    try {
      swiper.updateSlides?.()
      swiper.updateSize?.()
      swiper.update?.()

      if (shouldSync) {
        await syncSwiperToOrder()
      }
    } catch (error) {
      logger.error('Failed to refresh swiper:', error)
    }
  }

  /**
   * Синхронизирует слайдер с текущим выбранным заказом
   */
  async function syncSwiperToOrder(): Promise<void> {
    // Если один слайд или уже обновляется, пропускаем
    if (!hasMultipleOrders.value || isSwiperUpdating.value || !swiperContainerRef.value) {
      return
    }

    const targetIndex = currentOrderIndex.value

    const swiper = getSwiperInstance()
    if (!swiper) return

    const currentIndex = swiper.activeIndex ?? swiper.realIndex ?? 0

    if (currentIndex !== targetIndex) {
      await withSwiperUpdateLock(() => {
        swiper?.slideTo(targetIndex, SWIPER_CONFIG.SLIDE_SPEED)
        currentSlideIndex.value = targetIndex
      }, SWIPER_CONFIG.ANIMATION_DELAY)
    }
  }

  /**
   * Переключает слайд на указанный заказ
   */
  async function slideToOrder(orderId: string, speed: number = 0): Promise<void> {
    const targetIndex = navigationOrders.value.findIndex(
      (o) => String(o.id) === orderId,
    )

    if (targetIndex < 0) return

    const swiper = getSwiperInstance()
    if (!swiper) return

    await withSwiperUpdateLock(() => {
      swiper.slideTo(targetIndex, speed)
      currentSlideIndex.value = targetIndex
    }, SWIPER_CONFIG.UPDATE_DELAY)
  }

  /**
   * Обновляет параметры swiper (пересоздает компонент)
   */
  async function updateSwiperParams(): Promise<void> {
    // Увеличиваем ключ для пересоздания компонента
    swiperKey.value++

    // Ждем пересоздания
    await nextTick()
    await nextTick()

    // Даем время на инициализацию
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        refreshSwiperInstance(true).finally(resolve)
      }, SWIPER_CONFIG.INIT_DELAY)
    })
  }

  /**
   * Обработчик изменения активного слайда
   */
  function getActiveIndexFromEvent(event: CustomEvent): number | null {
    if (!hasMultipleOrders.value || isSwiperUpdating.value) {
      return null
    }

    // swiper-container web component передает swiper в detail[0]
    const detailEntry = Array.isArray(event.detail) ? event.detail[0] : null
    if (!detailEntry) return null
    const swiper = (detailEntry as any).swiper ?? detailEntry

    return swiper.activeIndex ?? swiper.realIndex ?? 0
  }

  return {
    // Refs
    swiperContainerRef,
    currentSlideIndex,
    isSwiperUpdating,
    swiperKey,

    // Computed
    navigationCount,
    hasMultipleOrders,
    currentOrderIndex,
    swiperConfig,
    swiperBreakpoints,
    swiperClasses,

    // Methods
    getSwiperInstance,
    refreshSwiperInstance,
    syncSwiperToOrder,
    slideToOrder,
    updateSwiperParams,
    withSwiperUpdateLock,
    getActiveIndexFromEvent,
  }
}

